import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { transferAchPositivePayRulesListAchPositivePayRulesByBankAccountOptions } from '@column/api';
import { Button, Fade, Icon, formatNumber } from '@column/column-ui-kit';

import { CustomPageContext, PageTabs, useBreadcrumbTitle } from '~/app/Layout';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { CopyableValue } from '~/components';
import { SimulateTransferModal } from '~/components/Dialog';
import { LogoLoading } from '~/elements';
import { useBankAccount } from '~/hooks';
import { usePinnedBankAccounts } from '~/lib/api/bankAccounts';
import { useNavigate } from '~/lib/navigation';
import { BankAccount } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner, Text } from '~/styles';

import { ButtonPin } from './_components/ButtonPin';
import { deriveIsBusinessAccount } from './achPositivePayEligibility';

const ACH_POSITIVE_PAY_TAB = 'ach-positive-pay';

interface Params {
  id: string;
}

export interface BankAccountSectionProps {
  bankAccount: BankAccount;
  /** `true` when the owning entity is a business, `false` when confirmed non-business, `undefined` while unknown. */
  isBusinessAccount?: boolean;
}

const StyledFade = styled(Fade)`
  margin-top: 10%;
`;

// Below desktop, the two metric groups use `display: contents` so all cells flow into the single
// MetricsRow grid (1 col → 2 col → 3 col); at desktop they become side-by-side grids split by a
// vertical rule. Breakpoints match theme.breakpoints (mobile 600, tablet 768, desktop 1024).
const METRICS_ROW =
  'grid grid-cols-[minmax(0,1fr)] gap-x-6 gap-y-4 pt-4 ' +
  '[&>*:first-child>*:first-child]:border-t-0 [&>*:first-child>*:first-child]:pt-0 ' +
  'min-[600px]:grid-cols-2 min-[768px]:grid-cols-3 ' +
  'min-[1024px]:flex min-[1024px]:items-stretch min-[1024px]:gap-6';

const METRICS_GROUP =
  'contents min-[1024px]:grid min-[1024px]:flex-1 min-[1024px]:grid-cols-3 min-[1024px]:gap-x-6 min-[1024px]:gap-y-4';

// Stacked (single-column) cells are separated by a top divider; multi-column and desktop layouts
// rely on the grid gap and vertical rule instead.
const METRIC_CELL = 'border-t border-border pt-4 min-[600px]:border-t-0 min-[600px]:pt-0';

const METRICS_SEPARATOR =
  'hidden min-[1024px]:block min-[1024px]:w-px min-[1024px]:self-stretch min-[1024px]:bg-border';

// Maps a bank account status to a display label and accent color. Colors are shared by both the
// value text and the dot marker so they always match. `open` reads as active (seafoam), `frozen` as
// a warning/hold state (orange), and `closed` as terminal (red).
const getStatusDisplay = (status: string): { color: string; label: string } => {
  switch (status) {
    case 'open':
      return { color: 'var(--color-seafoam-600)', label: 'Open' };
    case 'frozen':
      return { color: 'var(--color-orange-600)', label: 'Frozen' };
    case 'closed':
      return { color: 'var(--color-red-600)', label: 'Closed' };
    default:
      return { color: 'var(--color-gray-500)', label: status.charAt(0).toUpperCase() + status.slice(1) };
  }
};

export const PageBankAccountSection: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const {
    response: bankAccount,
    createRequest: fetchBankAccount,
    isLoading,
  } = useBankAccount({
    onError: (error) => {
      navigate(ROUTE.BANK_ACCOUNTS);

      addDangerNotification({
        content: error.message,
      });
    },
  });

  const { isSandbox, currentUser } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const { id } = useParams<keyof Params>() as Params;
  const { isPinned, togglePin } = usePinnedBankAccounts({ skipFetch: true });
  const [isSandboxModalOpen, setIsSandboxModalOpen] = useState<boolean>(false);

  const eligibilityQuery = useQuery({
    ...transferAchPositivePayRulesListAchPositivePayRulesByBankAccountOptions({
      path: { bank_account_id: bankAccount?.id as string },
    }),
    enabled: !!bankAccount?.id,
    retry: false,
  });
  const isBusinessAccount = deriveIsBusinessAccount(eligibilityQuery);

  // `status` is returned by the API (see BankAccountWithDetails in openapi.yaml) but the generated
  // `~/typings/API.ts` type is stale and omits it, so read it via a narrow local cast.
  const status = (bankAccount as (BankAccount & { status?: string }) | undefined)?.status;

  const visibleEntries = useMemo(
    () => entries?.filter((entry) => isBusinessAccount === true || !entry.path?.endsWith(ACH_POSITIVE_PAY_TAB)),
    [entries, isBusinessAccount]
  );

  useBreadcrumbTitle(id ? bankAccount?.displayName || bankAccount?.description : 'Create Account');

  useLayoutEffect(() => {
    if (id) {
      fetchBankAccount({ id });
    }
  }, [id]);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isSandbox: state.isSandbox,
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        (newState, oldState) => {
          if (!currentUser) {
            return;
          }

          if (newState.isSandbox !== oldState.isSandbox || newState.defaultPlatformId !== oldState.defaultPlatformId) {
            fetchBankAccount({ id });
          }
        }
      ),
    []
  );

  const outletContext = useMemo(
    () => (bankAccount ? { bankAccount, isBusinessAccount } : undefined),
    [bankAccount, isBusinessAccount]
  );

  return (
    <>
      <SimulateTransferModal
        isOpen={isSandboxModalOpen}
        onClose={() => setIsSandboxModalOpen(false)}
        accountNumberId={bankAccount?.defaultAccountNumberId}
        onSuccess={() => {
          setTimeout(() => {
            fetchBankAccount({ id });
          }, 200);
        }}
      />

      <StyledFade show={isLoading && !bankAccount}>
        <LogoLoading />
      </StyledFade>
      <Fade show={!isLoading || !!bankAccount}>
        <PageTabs entries={visibleEntries} />
        <PageActions>
          {id && isSandbox && (
            <Button onClick={() => setIsSandboxModalOpen(true)} size="small" icon={<Icon.Sandbox />}>
              Simulate Transfer
            </Button>
          )}

          {id && (
            <ButtonPin
              variant="secondary"
              isPinned={isPinned(id)}
              onClick={() => togglePin(id, bankAccount?.displayName ?? bankAccount?.description ?? '')}
            >
              Pin
            </ButtonPin>
          )}
        </PageActions>

        {id && (
          <div className="border-b border-border">
            <Inner px="var(--page-margin)" pt={24} pb={16}>
              <div>
                <div className="pb-4">
                  <Flex alignItems="baseline" gap={6}>
                    <Text
                      size={24}
                      lineHeight="24px"
                      weight="semibold"
                      letterSpacing="tightest"
                      color={(bankAccount?.balances?.availableAmount ?? 0) < 0 ? 'danger' : 'foreground'}
                    >
                      {formatNumber(bankAccount?.balances?.availableAmount)}
                    </Text>
                    <Text size={14} weight="medium" color="lighter">
                      USD
                    </Text>
                  </Flex>
                  <Text as="div" size={14} color="light" margin="6px 0 0">
                    Available Balance
                  </Text>
                </div>
                <div className="h-px bg-border" />
                <div className={METRICS_ROW}>
                  <div className={METRICS_GROUP}>
                    {[
                      {
                        label: 'Pending',
                        value: formatNumber(bankAccount?.balances?.pendingAmount),
                        negative: (bankAccount?.balances?.pendingAmount ?? 0) < 0,
                      },
                      {
                        label: 'Locked',
                        value: formatNumber(bankAccount?.balances?.lockedAmount),
                        negative: (bankAccount?.balances?.lockedAmount ?? 0) < 0,
                      },
                      {
                        label: 'Holding',
                        value: formatNumber(bankAccount?.balances?.holdingAmount),
                        negative: (bankAccount?.balances?.holdingAmount ?? 0) < 0,
                      },
                    ].map(({ label, value, negative }) => (
                      <div className={METRIC_CELL} key={label}>
                        <Text as="div" size={14} color="light" whiteSpace="nowrap">
                          {label}
                        </Text>
                        <Text
                          as="div"
                          size={16}
                          weight="medium"
                          color={negative ? 'danger' : 'foreground'}
                          margin="5px 0 0"
                        >
                          {value}
                        </Text>
                      </div>
                    ))}
                  </div>

                  <div className={METRICS_SEPARATOR} />

                  <div className={METRICS_GROUP}>
                    {[
                      { label: 'Routing Number', value: bankAccount?.routingNumber, align: 'left' as const },
                      { label: 'Account Number', value: bankAccount?.defaultAccountNumber, align: 'left' as const },
                    ].map(({ label, value, align }) => (
                      <div className={METRIC_CELL} key={label}>
                        <Text as="div" size={14} color="light" whiteSpace="nowrap">
                          {label}
                        </Text>
                        <div style={{ marginTop: 5 }}>
                          <CopyableValue value={value} align={align} />
                        </div>
                      </div>
                    ))}
                    {status &&
                      (() => {
                        const { color, label } = getStatusDisplay(status);
                        return (
                          <div className={METRIC_CELL}>
                            <Text as="div" size={14} color="light" whiteSpace="nowrap">
                              Status
                            </Text>
                            <Flex alignItems="center" gap={6} style={{ marginTop: 5 }}>
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: color,
                                  flexShrink: 0,
                                }}
                              />
                              <Text as="span" size={16} weight="medium" color={color}>
                                {label}
                              </Text>
                            </Flex>
                          </div>
                        );
                      })()}
                  </div>
                </div>
              </div>
            </Inner>
          </div>
        )}
        {outletContext && <Outlet context={outletContext} />}
      </Fade>
    </>
  );
};
